import { useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CalendarIcon, AlertCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { useEVM } from '@/context/EVMContext';
import { EVMData } from '@/types/evm';
import { Container } from '@/components/Layout/Container';
import { SecondaryHeader } from '@/components/Layout/Header/SecondaryHeader';
import { captureEvent, EVENTS, TOOL_IDS, TOOL_NAMES } from '@/lib/posthog';
import { SCurveChart } from '@/components/EVM/Charts/SCurveChart';
import {
  calculateAdvancedMetrics,
  formatCurrencyValue,
  formatIndexValue,
  formatPercentageValue,
  type AdvancedEVMMetrics,
  type ForecastScenario,
  type HealthIndicator,
} from '@/utils/advanced-evm-calculations';

interface ValidationError {
  field: string;
  message: string;
}

export function EVMTool() {
  const { state, dispatch } = useEVM();
  const { data } = state;

  // Track tool view on mount
  useEffect(() => {
    captureEvent(EVENTS.TOOL_VIEWED, {
      tool_id: TOOL_IDS.EVM,
      tool_name: TOOL_NAMES[TOOL_IDS.EVM],
    });
  }, []);

  // Calculate advanced metrics
  const advancedMetrics = useMemo(() => calculateAdvancedMetrics(data), [data]);

  // Validate inputs (using useMemo to avoid cascading renders)
  const validationErrors = useMemo(() => {
    const errors: ValidationError[] = [];

    // Validate non-negative values
    if (data.bac !== null && data.bac < 0) {
      errors.push({ field: 'bac', message: 'BAC must be non-negative' });
    }
    if (data.ac !== null && data.ac < 0) {
      errors.push({ field: 'ac', message: 'Actual Cost must be non-negative' });
    }
    if (data.pv !== null && data.pv < 0) {
      errors.push({ field: 'pv', message: 'Planned Value must be non-negative' });
    }
    if (data.ev !== null && data.ev < 0) {
      errors.push({ field: 'ev', message: 'Earned Value must be non-negative' });
    }

    // Validate EV <= BAC
    if (data.ev !== null && data.bac !== null && data.ev > data.bac) {
      errors.push({ field: 'ev', message: 'Earned Value cannot exceed BAC' });
    }

    // Validate date ordering
    if (data.startDate && data.endDate && data.startDate > data.endDate) {
      errors.push({ field: 'endDate', message: 'End date must be after start date' });
    }

    return errors;
  }, [data]);

  const handleChange = (field: keyof EVMData, value: EVMData[keyof EVMData]) => {
    dispatch({
      type: 'UPDATE_DATA',
      payload: { [field]: value }
    });
  };

  const hasError = (field: string) => validationErrors.some(e => e.field === field);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SecondaryHeader />

      <main className="flex-1">
        <Container>
          <div className="space-y-6 py-8">
            {/* Validation Alerts */}
            {validationErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <ul className="list-disc pl-4">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Project Health Status */}
            <ProjectHealthBanner
              status={advancedMetrics.healthStatus}
              indicators={advancedMetrics.healthIndicators}
            />

            {/* Project Details */}
            <Card>
              <CardHeader>
                <CardTitle>Project Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="projectName">Project Name</Label>
                      <Input
                        id="projectName"
                        value={data.projectName}
                        onChange={(e) => handleChange('projectName', e.target.value)}
                        placeholder="Enter project name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clientName">Client Name</Label>
                      <Input
                        id="clientName"
                        value={data.clientName}
                        onChange={(e) => handleChange('clientName', e.target.value)}
                        placeholder="Enter client name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Project Start Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !data.startDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {data.startDate ? format(data.startDate, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={data.startDate ?? undefined}
                            onSelect={(date) => handleChange('startDate', date ?? null)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Project End Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !data.endDate && "text-muted-foreground",
                              hasError('endDate') && "border-destructive"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {data.endDate ? format(data.endDate, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={data.endDate ?? undefined}
                            onSelect={(date) => handleChange('endDate', date ?? null)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div>
                    <Label>Reporting Period</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !data.reportingPeriodStart && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {data.reportingPeriodStart ? format(data.reportingPeriodStart, "PPP") : <span>Start date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={data.reportingPeriodStart ?? undefined}
                            onSelect={(date) => handleChange('reportingPeriodStart', date ?? null)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !data.reportingPeriodEnd && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {data.reportingPeriodEnd ? format(data.reportingPeriodEnd, "PPP") : <span>End date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={data.reportingPeriodEnd ?? undefined}
                            onSelect={(date) => handleChange('reportingPeriodEnd', date ?? null)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Financial Metrics */}
            <Card>
              <CardHeader>
                <CardTitle>Financial Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="bac">Budget at Completion (BAC)</Label>
                      <MetricTooltip content="The total budget allocated for the project. This value remains constant throughout the project." />
                    </div>
                    <Input
                      id="bac"
                      type="number"
                      min="0"
                      value={data.bac ?? ''}
                      onChange={(e) => handleChange('bac', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Enter BAC"
                      className={cn(hasError('bac') && "border-destructive")}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="ac">Actual Cost (AC)</Label>
                      <MetricTooltip content="The actual cost incurred for the work completed. Enter the total costs to date." />
                    </div>
                    <Input
                      id="ac"
                      type="number"
                      min="0"
                      value={data.ac ?? ''}
                      onChange={(e) => handleChange('ac', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Enter AC"
                      className={cn(hasError('ac') && "border-destructive")}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="pv">Planned Value (PV)</Label>
                      <MetricTooltip content="The estimated value of work planned to be completed by a specific date. Formula: PV = (Planned % Complete) x BAC." />
                    </div>
                    <Input
                      id="pv"
                      type="number"
                      min="0"
                      value={data.pv ?? ''}
                      onChange={(e) => handleChange('pv', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Enter PV"
                      className={cn(hasError('pv') && "border-destructive")}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="ev">Earned Value (EV)</Label>
                      <MetricTooltip content="The estimated value of the actual work completed to date. Formula: EV = (Actual % Complete) x BAC." />
                    </div>
                    <Input
                      id="ev"
                      type="number"
                      min="0"
                      value={data.ev ?? ''}
                      onChange={(e) => handleChange('ev', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Enter EV"
                      className={cn(hasError('ev') && "border-destructive")}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Variances */}
            <Card>
              <CardHeader>
                <CardTitle>Variances</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                  <MetricDisplay
                    label="Schedule Variance (SV)"
                    value={advancedMetrics.scheduleVariance}
                    formatter={formatCurrencyValue}
                    tooltip="The difference between Earned Value (EV) and Planned Value (PV). Formula: SV = EV - PV. A negative value indicates the project is behind schedule."
                    showTrend
                    invertTrend={false}
                  />
                  <MetricDisplay
                    label="Cost Variance (CV)"
                    value={advancedMetrics.costVariance}
                    formatter={formatCurrencyValue}
                    tooltip="The difference between Earned Value (EV) and Actual Cost (AC). Formula: CV = EV - AC. A negative value indicates the project is over budget."
                    showTrend
                    invertTrend={false}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Performance Indices */}
            <Card>
              <CardHeader>
                <CardTitle>Performance Indices</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                  <MetricDisplay
                    label="Schedule Performance Index (SPI)"
                    value={advancedMetrics.spi}
                    formatter={formatIndexValue}
                    tooltip="A measure of schedule efficiency. Formula: SPI = EV / PV. Values below 1 indicate the project is behind schedule."
                    showTrend
                    trendBaseline={1}
                    invertTrend={false}
                  />
                  <MetricDisplay
                    label="Cost Performance Index (CPI)"
                    value={advancedMetrics.cpi}
                    formatter={formatIndexValue}
                    tooltip="A measure of cost efficiency. Formula: CPI = EV / AC. Values below 1 indicate the project is over budget."
                    showTrend
                    trendBaseline={1}
                    invertTrend={false}
                  />
                  <MetricDisplay
                    label="To-Complete Performance Index (TCPI)"
                    value={advancedMetrics.tcpi}
                    formatter={formatIndexValue}
                    tooltip="The cost performance required to complete the remaining work within budget. Formula: TCPI = (BAC - EV) / (BAC - AC). Values above 1 indicate higher efficiency is needed."
                    showTrend
                    trendBaseline={1}
                    invertTrend={true}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Forecast Scenarios */}
            <Card>
              <CardHeader>
                <CardTitle>Forecast Scenarios</CardTitle>
                <CardDescription>
                  Multiple forecasting methods to estimate final project cost
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ForecastCard forecast={advancedMetrics.forecasts.cpiBased} />
                  <ForecastCard forecast={advancedMetrics.forecasts.spiBased} />
                  <ForecastCard forecast={advancedMetrics.forecasts.composite} />
                </div>
              </CardContent>
            </Card>

            {/* S-Curve Chart */}
            <Card>
              <CardHeader>
                <CardTitle>S-Curve Analysis</CardTitle>
                <CardDescription>
                  Visual representation of planned vs earned value over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SCurveChart data={data} height={350} />
              </CardContent>
            </Card>

            {/* Progress Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Progress Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricDisplay
                    label="Percent Complete"
                    value={advancedMetrics.percentComplete}
                    formatter={formatPercentageValue}
                    tooltip="The percentage of work completed based on earned value. Formula: (EV / BAC) x 100."
                  />
                  <MetricDisplay
                    label="Percent Scheduled"
                    value={advancedMetrics.percentScheduled}
                    formatter={formatPercentageValue}
                    tooltip="The percentage of work that should have been completed by now. Formula: (PV / BAC) x 100."
                  />
                  <MetricDisplay
                    label="Percent Spent"
                    value={advancedMetrics.percentSpent}
                    formatter={formatPercentageValue}
                    tooltip="The percentage of budget spent so far. Formula: (AC / BAC) x 100."
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </Container>
      </main>
    </div>
  );
}

// Helper Components

function MetricTooltip({ content }: { content: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface MetricDisplayProps {
  label: string;
  value: number | null;
  formatter: (value: number | null, fallback?: string) => string;
  tooltip: string;
  showTrend?: boolean;
  trendBaseline?: number;
  invertTrend?: boolean;
}

function MetricDisplay({
  label,
  value,
  formatter,
  tooltip,
  showTrend = false,
  trendBaseline = 0,
  invertTrend = false,
}: MetricDisplayProps) {
  const formattedValue = formatter(value);
  const isNA = value === null;

  let trendDirection: 'up' | 'down' | 'neutral' = 'neutral';
  if (showTrend && value !== null) {
    const diff = value - trendBaseline;
    if (Math.abs(diff) > 0.001) {
      trendDirection = diff > 0 ? 'up' : 'down';
      if (invertTrend) {
        trendDirection = trendDirection === 'up' ? 'down' : 'up';
      }
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label>{label}</Label>
        <MetricTooltip content={tooltip} />
      </div>
      <div className="text-2xl font-semibold flex items-center gap-2">
        <span className={cn(isNA && "text-muted-foreground")}>{formattedValue}</span>
        {showTrend && !isNA && <TrendIndicator direction={trendDirection} />}
      </div>
    </div>
  );
}

function TrendIndicator({ direction }: { direction: 'up' | 'down' | 'neutral' }) {
  if (direction === 'neutral') return null;
  return (
    <span
      className={cn(
        "text-sm",
        direction === 'up' ? "text-green-500" : "text-red-500"
      )}
    >
      {direction === 'up' ? "↑" : "↓"}
    </span>
  );
}

function ForecastCard({ forecast }: { forecast: ForecastScenario }) {
  const hasValidData = forecast.eac !== null;

  return (
    <div className="p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-sm">{forecast.label}</h4>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{forecast.description}</p>
      {hasValidData ? (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">EAC</span>
            <span className="font-semibold">{formatCurrencyValue(forecast.eac)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">ETC</span>
            <span className="font-medium">{formatCurrencyValue(forecast.etc)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">VAC</span>
            <span className={cn(
              "font-medium",
              (forecast.vac ?? 0) >= 0 ? "text-green-600" : "text-red-600"
            )}>
              {formatCurrencyValue(forecast.vac)}
            </span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Enter data to calculate forecast</p>
      )}
    </div>
  );
}

interface ProjectHealthBannerProps {
  status: AdvancedEVMMetrics['healthStatus'];
  indicators: HealthIndicator[];
}

function ProjectHealthBanner({ status, indicators }: ProjectHealthBannerProps) {
  // Don't show if no meaningful data
  const hasData = indicators.some(i => i.value !== null);
  if (!hasData) return null;

  const statusConfig = {
    on_track: {
      icon: CheckCircle2,
      color: 'text-green-600',
      bgColor: 'bg-green-50 border-green-200',
      label: 'On Track',
    },
    at_risk: {
      icon: AlertTriangle,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50 border-yellow-200',
      label: 'At Risk',
    },
    critical: {
      icon: AlertCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50 border-red-200',
      label: 'Critical',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Card className={cn("border", config.bgColor)}>
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className={cn("h-6 w-6", config.color)} />
            <div>
              <p className={cn("font-semibold", config.color)}>
                Project Status: {config.label}
              </p>
              <p className="text-sm text-muted-foreground">
                Based on current performance indices
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {indicators.filter(i => i.value !== null).map((indicator, index) => (
              <Badge
                key={index}
                variant={indicator.status === 'good' ? 'default' : indicator.status === 'warning' ? 'secondary' : 'destructive'}
                className="text-xs"
              >
                {indicator.metric.split('(')[0].trim()}: {formatIndexValue(indicator.value)}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
